import type {
  BrandingId, IntentId, OccasionId, OrderTopicId, QtyBandId, QuestionTopicId, RecipientId, StepId, TimingId,
} from "./ids";
import type { ContextItem, OpenContext } from "./context";

/**
 * Bozza e stato della richiesta guidata: reducer puro, senza React.
 *
 * Regole che i test fissano:
 * - il consenso E (`marketingConsent`) parte e torna sempre `false`, non viene mai
 *   impostato da un contesto e NON rende la bozza «toccata»: spuntarlo non deve
 *   cambiare nient'altro (finalità E indipendente da tutto il resto);
 * - un contesto (carrello, scheda prodotto) si applica solo a una bozza intatta;
 *   su una bozza toccata resta in sospeso e l'utente decide se unirlo;
 * - dopo un errore del server non si perde nulla; dopo un successo si azzera tutto.
 */

export interface Draft {
  intent: IntentId | null;
  boxes: string[];                  // slug in ordine di scelta
  qty: Record<string, string>;      // slug → cifre digitate ("" = pezzi non indicati)
  box: string | null;               // la confezione di una domanda
  qtyBand: QtyBandId | null;
  timing: TimingId | null;
  date: string;                     // "YYYY-MM-DD" | ""
  occasion: OccasionId | null;
  recipients: RecipientId[];
  branding: BrandingId[];
  orderTopic: OrderTopicId | null;
  questionTopics: QuestionTopicId[];
  note: string;
  name: string;
  company: string;
  phone: string;
  email: string | null;             // null = mai toccata: si mostra l'email dell'account, se c'è
  marketingConsent: boolean;
  titles: Record<string, string>;   // slug → titolo arrivato col contesto (ripiego se il catalogo non c'è)
}

export type FieldName = "name" | "email" | "company" | "phone";

export interface FieldErrors {
  intent?: true;
  date?: true;
  name?: true;
  email?: true;
  phone?: true;
  note?: "required" | "tooLong";
}

export interface StampView {
  slug: string;
  title: string;
  imageUrl: string;
}

export interface SentSnapshot {
  message: string;
  headline: string | null;
  email: string;
  name: string;
  company: string;
  phone: string;
  stamps: StampView[];
  sentAt: string;                   // ISO, per la data del timbro
}

export type Phase = "editing" | "sending" | "error" | "success";

export interface ComposerState {
  draft: Draft;
  touched: boolean;
  step: StepId;
  /** Arrivati a un passo con «Modifica» dal riepilogo: «Continua» riporta al riepilogo. */
  editReturn: boolean;
  errors: FieldErrors;
  /** Dopo il primo tentativo d'invio gli errori si aggiornano mentre si scrive. */
  attempted: boolean;
  phase: Phase;
  snapshot: SentSnapshot | null;
  /** Quale aiuto contestuale mostrare nei dettagli. */
  contextHint: "page" | "cart" | null;
  /** Contesto del carrello arrivato su una bozza già toccata: si propone di unirlo. */
  pendingCart: ContextItem[] | null;
  /** Confezioni la cui quantità è stata portata al minimo d'ordine (avviso visibile). */
  qtyRaised: Record<string, number>;
  /** Avviso da annunciare ai lettori di schermo (solo id e numeri: il testo si compone al momento). */
  notice: { qtyRaised: number } | null;
}

export function emptyDraft(): Draft {
  return {
    intent: null, boxes: [], qty: {}, box: null, qtyBand: null, timing: null, date: "",
    occasion: null, recipients: [], branding: [], orderTopic: null, questionTopics: [], note: "",
    name: "", company: "", phone: "", email: null, marketingConsent: false, titles: {},
  };
}

export function initialState(): ComposerState {
  return {
    draft: emptyDraft(), touched: false, step: 1, editReturn: false, errors: {}, attempted: false,
    phase: "editing", snapshot: null, contextHint: null, pendingCart: null, qtyRaised: {}, notice: null,
  };
}

export type Action =
  | { type: "intent"; id: IntentId }
  | { type: "toggleBox"; slug: string; on: boolean; title?: string }
  | { type: "singleBox"; slug: string; title?: string }
  | { type: "qty"; slug: string; raw: string }
  | { type: "qtyStep"; slug: string; delta: 1 | -1; moq: number | null }
  | { type: "qtyCommit"; slug: string; moq: number | null }
  | { type: "qtyBand"; id: QtyBandId }
  | { type: "timing"; id: TimingId }
  | { type: "date"; iso: string }
  | { type: "occasion"; id: OccasionId }
  | { type: "toggleRecipient"; id: RecipientId; on: boolean }
  | { type: "toggleBranding"; id: BrandingId; on: boolean }
  | { type: "orderTopic"; id: OrderTopicId }
  | { type: "toggleQuestionTopic"; id: QuestionTopicId; on: boolean }
  | { type: "note"; value: string }
  | { type: "field"; field: FieldName; value: string }
  | { type: "consent"; value: boolean }
  | { type: "goTo"; step: StepId; edit?: boolean }
  | { type: "context"; ctx: OpenContext }
  | { type: "mergeCart" }
  | { type: "dismissCart" }
  | { type: "errors"; errors: FieldErrors; attempted?: boolean }
  | { type: "sending" }
  | { type: "sent"; snapshot: SentSnapshot }
  | { type: "failed" }
  | { type: "dismissSuccess" };

const MAX_QTY_DIGITS = 5;

function toggle<T>(list: readonly T[], value: T, on: boolean): T[] {
  const has = list.includes(value);
  if (on && !has) return [...list, value];
  if (!on && has) return list.filter((v) => v !== value);
  return [...list];
}

/** Tiene il titolo della confezione scelta: se il catalogo di un'altra lingua non è ancora arrivato, il messaggio non la perde. */
function withTitle(titles: Record<string, string>, slug: string, title?: string): Record<string, string> {
  return title && titles[slug] !== title ? { ...titles, [slug]: title } : titles;
}

function withoutKey<T>(rec: Record<string, T>, key: string): Record<string, T> {
  if (!(key in rec)) return rec;
  const next = { ...rec };
  delete next[key];
  return next;
}

/** Applica il contesto a una bozza intatta (le confezioni e, dal carrello, l'intento e i pezzi). */
function applyContext(state: ComposerState, ctx: OpenContext): ComposerState {
  const slugs = ctx.items.map((i) => i.slug);
  const qty: Record<string, string> = {};
  const titles: Record<string, string> = {};
  for (const it of ctx.items) {
    if (it.qty) qty[it.slug] = String(it.qty);
    if (it.title) titles[it.slug] = it.title;
  }
  const draft: Draft = {
    ...emptyDraft(),
    // I dati personali e il consenso non dipendono dal contesto: restano come sono.
    name: state.draft.name, company: state.draft.company, phone: state.draft.phone, email: state.draft.email,
    marketingConsent: state.draft.marketingConsent,
    intent: ctx.intent,
    boxes: slugs,
    box: slugs[0] ?? null,
    qty,
    titles,
  };
  // Dal carrello e dalla CTA di Collezioni l'intento è già deciso: si parte dai
  // dettagli. Dalla scheda prodotto si sceglie ancora l'argomento, con la
  // confezione già pronta.
  const decided = ctx.source !== "page" && !!ctx.intent && ctx.intent !== "other" && ctx.intent !== "order";
  const step: StepId = decided ? 2 : 1;
  return {
    ...state, draft, step, editReturn: false, errors: {}, attempted: false,
    contextHint: slugs.length && ctx.source !== "collections" ? ctx.source : null, pendingCart: null, qtyRaised: {}, notice: null,
  };
}

function edit(state: ComposerState, draft: Draft): ComposerState {
  // Una nuova modifica dopo un errore del server non cancella l'avviso: sparisce
  // solo al prossimo tentativo d'invio (nessun timer).
  return { ...state, draft, touched: true, notice: null };
}

export function composerReducer(state: ComposerState, action: Action): ComposerState {
  const d = state.draft;
  switch (action.type) {
    case "intent": {
      const errors = { ...state.errors };
      delete errors.intent;
      return { ...edit(state, { ...d, intent: action.id }), errors };
    }
    case "toggleBox": {
      const boxes = toggle(d.boxes, action.slug, action.on);
      return edit(state, { ...d, boxes, titles: withTitle(d.titles, action.slug, action.title) });
    }
    case "singleBox":
      return edit(state, { ...d, box: action.slug, titles: withTitle(d.titles, action.slug, action.title) });
    case "qty": {
      const raw = action.raw.replace(/\D/g, "").slice(0, MAX_QTY_DIGITS);
      return { ...edit(state, { ...d, qty: { ...d.qty, [action.slug]: raw } }), qtyRaised: withoutKey(state.qtyRaised, action.slug) };
    }
    case "qtyStep": {
      const cur = parseInt(d.qty[action.slug] ?? "", 10);
      const min = action.moq ?? 1;
      let next: string;
      if (Number.isNaN(cur)) next = action.delta > 0 ? String(min) : "";
      else if (action.delta > 0) next = String(Math.min(cur + 1, 99999));
      else next = cur - 1 < min ? String(min) : String(cur - 1);
      return { ...edit(state, { ...d, qty: { ...d.qty, [action.slug]: next } }), qtyRaised: withoutKey(state.qtyRaised, action.slug) };
    }
    case "qtyCommit": {
      // All'uscita dal campo: un numero sotto il minimo d'ordine diventa il minimo,
      // con un avviso visibile. Mai una quantità sotto il minimo nel messaggio.
      const cur = parseInt(d.qty[action.slug] ?? "", 10);
      if (Number.isNaN(cur) || cur === 0) {
        if ((d.qty[action.slug] ?? "") === "") return state;
        return { ...state, draft: { ...d, qty: { ...d.qty, [action.slug]: "" } } };
      }
      if (action.moq && cur < action.moq) {
        return {
          ...state,
          draft: { ...d, qty: { ...d.qty, [action.slug]: String(action.moq) } },
          qtyRaised: { ...state.qtyRaised, [action.slug]: action.moq },
          notice: { qtyRaised: action.moq },
        };
      }
      const clean = String(cur);
      return clean === d.qty[action.slug] ? state : { ...state, draft: { ...d, qty: { ...d.qty, [action.slug]: clean } } };
    }
    case "qtyBand":
      return edit(state, { ...d, qtyBand: action.id });
    case "timing": {
      const errors = { ...state.errors };
      delete errors.date;
      return { ...edit(state, { ...d, timing: action.id }), errors };
    }
    case "date": {
      const errors = { ...state.errors };
      delete errors.date;
      return { ...edit(state, { ...d, date: action.iso }), errors };
    }
    case "occasion":
      return edit(state, { ...d, occasion: action.id });
    case "toggleRecipient":
      return edit(state, { ...d, recipients: toggle(d.recipients, action.id, action.on) });
    case "toggleBranding":
      return edit(state, { ...d, branding: toggle(d.branding, action.id, action.on) });
    case "orderTopic":
      return edit(state, { ...d, orderTopic: action.id });
    case "toggleQuestionTopic":
      return edit(state, { ...d, questionTopics: toggle(d.questionTopics, action.id, action.on) });
    case "note":
      return edit(state, { ...d, note: action.value });
    case "field":
      return edit(state, { ...d, [action.field]: action.value });
    case "consent":
      // Finalità E: nessun effetto su «touched», contesto, avanzamento o completezza.
      return { ...state, draft: { ...d, marketingConsent: action.value } };
    case "goTo":
      return { ...state, step: action.step, editReturn: !!action.edit };
    case "context":
      if (!state.touched) return applyContext(state, action.ctx);
      // Bozza già iniziata: si tiene. Il carrello resta in sospeso e l'utente
      // può unirne le confezioni con un pulsante; la scheda prodotto si ignora.
      return action.ctx.source === "cart" && action.ctx.items.length
        ? { ...state, pendingCart: action.ctx.items }
        : state;
    case "mergeCart": {
      const items = state.pendingCart ?? [];
      const boxes = [...d.boxes];
      const qty = { ...d.qty };
      const titles = { ...d.titles };
      for (const it of items) {
        if (!boxes.includes(it.slug)) boxes.push(it.slug);
        if (it.qty && !qty[it.slug]) qty[it.slug] = String(it.qty);
        if (it.title && !titles[it.slug]) titles[it.slug] = it.title;
      }
      return { ...edit(state, { ...d, boxes, qty, titles }), pendingCart: null, contextHint: "cart" };
    }
    case "dismissCart":
      return { ...state, pendingCart: null };
    case "errors":
      return { ...state, errors: action.errors, attempted: action.attempted ?? state.attempted };
    case "sending":
      return { ...state, phase: "sending" };
    case "sent":
      return { ...initialState(), phase: "success", snapshot: action.snapshot };
    case "failed":
      return { ...state, phase: "error" };
    case "dismissSuccess":
      return initialState();
  }
}
